import React, { useMemo, useState } from "react";
import { AmplitudeEventSource } from "@analytics/Amplitude/AmplitudeEvents";
import { AmplitudeService } from "@analytics/Amplitude/AmplitudeService";
import LifeAtCheckbox from "@common/LifeAtCheckbox/LifeAtCheckbox";
import { DropdownMenuComponents } from "@common/LifeAtDropdownMenuV2/DropdownMenuComponents";
import { CallToActionContextType, useCallToActionContext } from "@contexts/CallToActionContext";
import { useUserContext } from "@contexts/UserContext";
import { UpgradeCTASource } from "@user/AmplitudeProSubsEvents";
import { FilterTasksByTagEvent } from "../AmplitudeTaskFilterEvents";
import { useTaskListContext } from "../TaskListContext";
import TagComponent from "./TagComponent";
import { TaskFilterButton } from "./TaskFilterButton";
import { AddNewTagDropdownItem, DefaultTagIndicator, DropdownItem, EditTagButton } from "./TaskTagDropdownComponents";

const FILTER_OPTIONS_LABEL = "Display tags:";
const TRIGGER_LABEL = "Tags";
const ALL_OPTION_LABEL = "All";

interface TaskTagFilterDropdownProps {
    readonly amplitudeEventSource: AmplitudeEventSource;
}
export function TaskTagFilterDropdown(props: TaskTagFilterDropdownProps) {
    const amplitudeService = AmplitudeService.getInstance();
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const { tags, filters, toggleTagFilter, clearTagFilters, defaultTagId } = useTaskListContext();
    const { userData } = useUserContext();
    const { setUpgradeCtaContext }: CallToActionContextType = useCallToActionContext();

    const isProUser = userData?.proUserInfo?.isProUser;

    const selectedTagIdSet = useMemo(() => {
        const ret = new Set<string>();
        for (const filter of filters) {
            if (filter.type === "tag") {
                ret.add(filter.tag);
            }
        }
        return ret;
    }, [filters]);

    return (
        <DropdownMenuComponents.Root onOpenChange={(isOpen: boolean) => setIsDropdownOpen(isOpen)}>
            <DropdownMenuComponents.Trigger>
                <TaskFilterButton
                    label={TRIGGER_LABEL}
                    count={filters.filter((filter) => filter.type === "tag").length}
                    isOpen={isDropdownOpen}
                />
            </DropdownMenuComponents.Trigger>
            <DropdownMenuComponents.Content>
                <DropdownMenuComponents.HeaderItem label={FILTER_OPTIONS_LABEL} />
                <DropdownItem onClick={() => clearTagFilters()}>
                    <LifeAtCheckbox checked={selectedTagIdSet.size === 0} />
                    {ALL_OPTION_LABEL}
                </DropdownItem>

                {tags.map(({ id: tagId, document: tag }) => (
                    <DropdownItem
                        key={tagId}
                        onClick={() => {
                            if (!isProUser) {
                                setUpgradeCtaContext("tags", UpgradeCTASource.FILTER_TAGS);
                            } else {
                                toggleTagFilter(tagId);
                                amplitudeService.logEvent(
                                    new FilterTasksByTagEvent({
                                        source: props.amplitudeEventSource,
                                        selectedTagCount: selectedTagIdSet.size,
                                    }),
                                );
                            }
                        }}
                        isDisabled={!isProUser}
                    >
                        <LifeAtCheckbox checked={selectedTagIdSet.has(tagId)} />
                        <TagComponent tag={tag} disableHover={true} />
                        {tagId === defaultTagId && <DefaultTagIndicator />}
                        <EditTagButton tagId={tagId} tag={tag} />
                    </DropdownItem>
                ))}
                <DropdownMenuComponents.Separator />

                <AddNewTagDropdownItem />
            </DropdownMenuComponents.Content>
        </DropdownMenuComponents.Root>
    );
}
